import * as React from 'react'
import { createPortal } from 'react-dom'

import {
  Box,
  Button,
  TextField,
  Tooltip,
  useToggle,
  useUnsafeCallback,
  useUnsafeEffect,
} from '@factorialco/deprecated-design-system'
import { $isLinkNode, TOGGLE_LINK_COMMAND } from '@lexical/link'
import { mergeRegister } from '@lexical/utils'
import {
  $getSelection,
  $isRangeSelection,
  BaseSelection,
  CLICK_COMMAND,
  COMMAND_PRIORITY_CRITICAL,
  COMMAND_PRIORITY_LOW,
  LexicalEditor,
  SELECTION_CHANGE_COMMAND,
} from 'lexical'
import isEmpty from 'lodash/isEmpty'

import { useEditor } from 'modules/core/components/TextEditor/useEditor'
import { getSelectedNode } from 'modules/core/components/TextEditor/utils/getSelectedNode'
import { isLinkAndLastNode } from 'modules/core/components/TextEditor/utils/isLinkAndLastNode'
import { sanitizeUrl, validateUrl } from 'modules/core/components/TextEditor/utils/url'

import './index.styles.css'

interface FloatingLinkEditorProps {
  editor: LexicalEditor
  anchorElem: HTMLElement
  closeLinkEditor: () => void
  missingLabel: string
  selectValue: string
  saveButtonLabel: string
  removeButtonLabel: string
  errorLabelFn: (validUrl: boolean, linkUrl: string) => string | undefined
}

function FloatingLinkEditor({
  closeLinkEditor,
  editor,
  anchorElem,
  missingLabel,
  selectValue,
  saveButtonLabel,
  removeButtonLabel,
  errorLabelFn,
}: FloatingLinkEditorProps) {
  const editorRef = React.useRef<HTMLDivElement | null>(null)
  const inputRef = React.useRef<HTMLInputElement | undefined | null>(null)
  const prevUrl = React.useRef('')
  const [linkUrl, setLinkUrl] = React.useState('')
  const [validUrl, setValidUrl] = React.useState(false)
  const [lastSelection, setLastSelection] = React.useState<BaseSelection | null>(null)
  const [saveTooltipOpen, toggleSaveTooltip] = useToggle()
  const [removeTooltipOpen, toggleRemoveTooltip] = useToggle()

  const updateLinkEditor = useUnsafeCallback(() => {
    const selection = $getSelection()

    if ($isRangeSelection(selection)) {
      const node = getSelectedNode(selection)
      const parent = node.getParent()

      if ($isLinkNode(parent)) {
        setLinkUrl(parent.getURL())
      } else if ($isLinkNode(node)) {
        setLinkUrl(node.getURL())
      } else {
        setLinkUrl('')
      }
    }

    const editorElem = editorRef.current
    const nativeSelection = window.getSelection()
    const activeElement = document.activeElement

    if (editorElem === null) {
      return
    }

    const rootElement = editor.getRootElement()

    if (
      selection !== null &&
      nativeSelection !== null &&
      rootElement !== null &&
      rootElement.contains(nativeSelection.anchorNode) &&
      editor.isEditable()
    ) {
      const domRect: DOMRect | undefined =
        nativeSelection.focusNode?.parentElement?.getBoundingClientRect()
      if (domRect) {
        domRect.y += 40
        setFloatingElemPosition(domRect, editorElem, anchorElem)
      }
      setLastSelection(selection)
    } else if (!activeElement || activeElement !== inputRef.current) {
      if (rootElement !== null) {
        setFloatingElemPosition(null, editorElem, anchorElem)
      }

      setLastSelection(null)
      setLinkUrl('')
    }

    return true
  }, [anchorElem, editor])

  useUnsafeEffect(() => {
    const scrollerElem = anchorElem.parentElement

    const update = () => {
      editor.getEditorState().read(() => {
        updateLinkEditor()
      })
    }

    window.addEventListener('resize', update)

    if (scrollerElem) {
      scrollerElem.addEventListener('scroll', update)
    }

    return () => {
      window.removeEventListener('resize', update)

      if (scrollerElem) {
        scrollerElem.removeEventListener('scroll', update)
      }
    }
  }, [anchorElem.parentElement, editor, updateLinkEditor])

  useUnsafeEffect(() => {
    return mergeRegister(
      editor.registerUpdateListener(({ editorState }) => {
        editorState.read(() => {
          updateLinkEditor()
        })
      }),

      editor.registerCommand(
        SELECTION_CHANGE_COMMAND,
        () => {
          updateLinkEditor()

          return true
        },
        COMMAND_PRIORITY_LOW
      )
    )
  }, [editor, updateLinkEditor])

  useUnsafeEffect(() => {
    editor.getEditorState().read(() => {
      updateLinkEditor()
    })
  }, [editor, updateLinkEditor])

  useUnsafeEffect(() => {
    if (prevUrl.current === '') {
      prevUrl.current = linkUrl
    }

    setValidUrl(validateUrl(linkUrl))
  }, [linkUrl])

  const saveNewUrl = () => {
    if (isEmpty(lastSelection) || !validUrl) return

    const newUrl = sanitizeUrl(linkUrl)
    editor.dispatchCommand(TOGGLE_LINK_COMMAND, {
      url: newUrl,
      target: '_blank',
    })
    prevUrl.current = newUrl
    closeLinkEditor()
  }

  return (
    <div className={'FloatingLinkEditorPlugin_floatingLinkEditor'} ref={editorRef}>
      <Box flexDirection="row" gap="s12">
        <Box minWidth="s240">
          <TextField
            placeholder="https://"
            value={linkUrl}
            error={errorLabelFn(validUrl, linkUrl)}
            onChange={setLinkUrl}
            getRef={(elem) => {
              inputRef.current = elem
            }}
            onKeyDown={(event) => {
              if (event.key === 'Enter') {
                event.preventDefault()
                saveNewUrl()
              } else if (event.key === 'Escape') {
                event.preventDefault()
                setLinkUrl(prevUrl.current)
              }
            }}
            missingLabel={missingLabel}
            selectValue={selectValue}
          />
        </Box>

        <Box style={{ height: '44px' }} flexDirection="row" alignItems="center" gap="s12">
          <Tooltip.Alt
            attachment="bottom center"
            open={saveTooltipOpen}
            toggle={toggleSaveTooltip}
            label={saveButtonLabel}
          >
            <Button
              disabled={!validUrl}
              type="secondary"
              onClick={() => saveNewUrl()}
              iconName="link"
            />
          </Tooltip.Alt>

          <Tooltip.Alt
            attachment="bottom center"
            open={removeTooltipOpen}
            toggle={toggleRemoveTooltip}
            label={removeButtonLabel}
          >
            <Button
              type="negative"
              onClick={() => editor.dispatchCommand(TOGGLE_LINK_COMMAND, null)}
              iconName="link-remove"
            />
          </Tooltip.Alt>
        </Box>
      </Box>
    </div>
  )
}

interface FloatingLinkEditorPluginProps {
  anchorElem?: HTMLElement | null
  missingLabel: string
  selectValue: string
  saveButtonLabel: string
  removeButtonLabel: string
  errorLabelFn: (validUrl: boolean, linkUrl: string) => string | undefined
}

export default function FloatingLinkEditorPlugin({
  anchorElem,
  missingLabel,
  selectValue,
  saveButtonLabel,
  removeButtonLabel,
  errorLabelFn,
}: FloatingLinkEditorPluginProps) {
  const anchorElement = anchorElem || document.body
  const editor = useEditor()
  const [isLink, setIsLink] = React.useState(false)

  const updateToolbar = useUnsafeCallback(() => {
    const selection = $getSelection()

    if ($isRangeSelection(selection)) {
      const { isLastNode, isLinkNode } = isLinkAndLastNode(selection)

      if (isLinkNode && !isLastNode) {
        setIsLink(true)
      } else {
        setIsLink(false)
      }
    }
  }, [])

  useUnsafeEffect(() => {
    return editor.registerCommand(
      CLICK_COMMAND,
      () => {
        updateToolbar()

        return false
      },
      COMMAND_PRIORITY_CRITICAL
    )
  }, [editor, updateToolbar])

  useUnsafeEffect(() => {
    return editor.registerCommand(
      SELECTION_CHANGE_COMMAND,
      () => {
        const selection = $getSelection()

        if ($isRangeSelection(selection)) {
          const { isLastNode, isLinkNode } = isLinkAndLastNode(selection)

          if (!isLinkNode || isLastNode) setIsLink(false)
        }

        return false
      },
      COMMAND_PRIORITY_CRITICAL
    )
  }, [editor, updateToolbar])

  useUnsafeEffect(() => {
    return editor.registerCommand(
      TOGGLE_LINK_COMMAND,
      (payload) => {
        setIsLink(payload !== null)

        return false
      },
      COMMAND_PRIORITY_CRITICAL
    )
  }, [editor, setIsLink])

  const closeLinkEditor = () => setIsLink(false)

  if (isLink && editor.isEditable()) {
    return createPortal(
      <FloatingLinkEditor
        closeLinkEditor={closeLinkEditor}
        editor={editor}
        anchorElem={anchorElement}
        missingLabel={missingLabel}
        selectValue={selectValue}
        saveButtonLabel={saveButtonLabel}
        removeButtonLabel={removeButtonLabel}
        errorLabelFn={errorLabelFn}
      />,
      anchorElement
    )
  }

  return null
}

const VERTICAL_GAP = 12
const HORIZONTAL_OFFSET = 6

function setFloatingElemPosition(
  targetRect: ClientRect | null,
  floatingElem: HTMLElement,
  anchorElem: HTMLElement,
  verticalGap: number = VERTICAL_GAP,
  horizontalOffset: number = HORIZONTAL_OFFSET
) {
  const scrollerElem = anchorElem.parentElement

  if (targetRect === null || !scrollerElem) {
    floatingElem.style.opacity = '0'
    floatingElem.style.top = '-10000px'
    floatingElem.style.left = '-10000px'

    return
  }

  const floatingElemRect = floatingElem.getBoundingClientRect()
  const anchorElementRect = anchorElem.getBoundingClientRect()
  const editorScrollerRect = scrollerElem.getBoundingClientRect()

  let top = targetRect.top - floatingElemRect.height - verticalGap
  let left = targetRect.left - horizontalOffset

  if (top < editorScrollerRect.top) {
    top += floatingElemRect.height + targetRect.height + verticalGap * 2
  }

  if (left + floatingElemRect.width > editorScrollerRect.right) {
    left = editorScrollerRect.right - floatingElemRect.width - horizontalOffset
  }

  top -= anchorElementRect.top
  left -= anchorElementRect.left

  floatingElem.style.opacity = '1'
  floatingElem.style.top = `${top}px`
  floatingElem.style.left = `${left}px`
}
